import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Modal } from "../components/Modal";
import { useApi } from "../lib/useApi";
import { ApiError } from "../lib/api";
import { usePolling } from "../lib/usePolling";
import type {
  Certificate,
  Ingress,
  K8sEvent,
  PodDetail,
  PodSummary,
  Workload,
  WorkloadActionResult,
} from "../lib/api";
import { useSetTopbarTabs } from "../components/TopbarTabs";
import { usePermissions } from "../lib/roles";

type Tab = "services" | "ingresses" | "certificates";
type Scope = "self" | "parent";

type WorkloadSelection = { kind: Workload["kind"]; name: string };
type EventsFor = { kind: string; name: string };

export function ObservabilityServicesPage() {
  const [tab, setTab] = useState<Tab>("services");

  useSetTopbarTabs([
    { id: "services", label: "Services", active: tab === "services", onSelect: () => setTab("services") },
    { id: "ingresses", label: "Ingresses", active: tab === "ingresses", onSelect: () => setTab("ingresses") },
    { id: "certificates", label: "Certificates", active: tab === "certificates", onSelect: () => setTab("certificates") },
  ]);

  return (
    <>
      <div className="page-head reveal" data-d="1">
        <div className="crumb">Observability · Services</div>
        <h1>Cluster topology</h1>
        <p>
          Live read of this workspace's namespace plus its parent (env) namespace where shared infra lives —
          ingress-nginx, redis, redpanda. Workloads, ingresses, and certificates from the Kubernetes API; drill in for
          events, env vars, pod status, and live logs.
        </p>
      </div>

      {tab === "services" && (
        <>
          <WorkloadsPanel scope="parent" label="Parent services" hint="env namespace · ingress-nginx, redis, redpanda" idx="01" />
          <WorkloadsPanel scope="self" label="Workloads" hint="this workspace's deployments + statefulsets" idx="02" />
        </>
      )}
      {tab === "ingresses" && <IngressesPanel />}
      {tab === "certificates" && <CertificatesPanel />}
    </>
  );
}

type SelKey = string; // `${kind}/${name}`

function selKey(w: { kind: string; name: string }): SelKey { return `${w.kind}/${w.name}`; }

function WorkloadsPanel({ scope, label, hint, idx }: { scope: Scope; label: string; hint: string; idx: string }) {
  const api = useApi();
  const perms = usePermissions();
  const [workloads, setWorkloads] = useState<Workload[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [selected, setSelected] = useState<WorkloadSelection | null>(null);
  const [eventsFor, setEventsFor] = useState<EventsFor | null>(null);
  const [autoRefresh, setAutoRefresh] = useState(true);

  // Multi-select for bulk operations (cloud-team only). Keyed on `${kind}/${name}`.
  const [picked, setPicked] = useState<Set<SelKey>>(new Set());
  const [bulkBusy, setBulkBusy] = useState<"restart" | "scale" | null>(null);
  const [bulkResults, setBulkResults] = useState<WorkloadActionResult[] | null>(null);
  const [scaleDialogOpen, setScaleDialogOpen] = useState(false);

  const load = useCallback(async () => {
    try {
      const next = await api.listWorkloads(scope);
      setWorkloads(next);
      setError(null);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : `${e}`);
    }
  }, [api, scope]);

  useEffect(() => { void load(); }, [load]);
  usePolling(load, 5000, autoRefresh);

  function togglePick(w: Workload) {
    setPicked((prev) => {
      const next = new Set(prev);
      const k = selKey(w);
      if (next.has(k)) next.delete(k); else next.add(k);
      return next;
    });
  }

  function pickAll(all: boolean) {
    setPicked(all && workloads ? new Set(workloads.map(selKey)) : new Set());
  }

  async function bulkRestart() {
    if (picked.size === 0 || !workloads) return;
    setBulkBusy("restart");
    setBulkResults(null);
    try {
      const targets = workloads
        .filter((w) => picked.has(selKey(w)))
        .map((w) => ({ kind: w.kind, name: w.name }));
      const { results } = await api.restartWorkloads(targets, scope);
      setBulkResults(results);
      setPicked(new Set());
      await load();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : `${e}`);
    } finally {
      setBulkBusy(null);
    }
  }

  async function bulkScale(replicas: number) {
    if (picked.size === 0 || !workloads) return;
    setBulkBusy("scale");
    setBulkResults(null);
    setScaleDialogOpen(false);
    try {
      const targets = workloads
        .filter((w) => picked.has(selKey(w)))
        .map((w) => ({ kind: w.kind, name: w.name }));
      const { results } = await api.scaleWorkloads(targets, replicas, scope);
      setBulkResults(results);
      setPicked(new Set());
      await load();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : `${e}`);
    } finally {
      setBulkBusy(null);
    }
  }

  const allChecked = workloads !== null && workloads.length > 0 && picked.size === workloads.length;
  const someChecked = picked.size > 0 && !allChecked;
  const showActions = perms.canActOnWorkloads;

  return (
    <>
      <section className="panel reveal" data-d="2">
        <header>
          <span className="idx">{idx}</span> {label}
          <span className="hint">{hint} · {autoRefresh ? "live · 5s" : "paused"}</span>
          <span className="spacer" />
          {showActions && picked.size > 0 && (
            <>
              <span className="mono faint" style={{ fontSize: 11, marginRight: 10 }}>{picked.size} selected</span>
              <button
                className={"btn btn-sm btn-primary" + (bulkBusy === "restart" ? " loading" : "")}
                disabled={bulkBusy !== null}
                onClick={() => void bulkRestart()}
              >
                <span className="glyph" /> Restart
              </button>{" "}
              <button
                className={"btn btn-sm" + (bulkBusy === "scale" ? " loading" : "")}
                disabled={bulkBusy !== null}
                onClick={() => setScaleDialogOpen(true)}
              >
                <span className="glyph" /> Scale to…
              </button>{" "}
              <button className="btn btn-sm btn-ghost" onClick={() => setPicked(new Set())}>Clear</button>{" "}
            </>
          )}
          <button className="btn btn-ghost btn-sm" onClick={() => setAutoRefresh((v) => !v)}>
            {autoRefresh ? "Pause" : "Resume"}
          </button>{" "}
          <button className="btn btn-ghost btn-sm" onClick={() => void load()}>
            <span className="glyph" /> Refresh
          </button>
        </header>
        <div className="body">
          {error && <Err msg={error} />}
          {bulkResults && (
            <div className="callout" style={{ marginBottom: 14, background: "var(--bg-panel-2)", border: "1px dashed var(--line-strong)" }}>
              <span className="mk">ok</span>
              <span style={{ fontSize: 11 }}>
                {bulkResults.filter((r) => r.ok).length} succeeded
                {bulkResults.some((r) => !r.ok) && (
                  <>, failed: {bulkResults.filter((r) => !r.ok).map((r) => `${r.kind}/${r.name} (${r.error})`).join("; ")}</>
                )}
              </span>
            </div>
          )}
          {!workloads ? <Loading /> : (
            <div style={{ border: "1px solid var(--line)" }}>
              <table className="grid">
                <thead>
                  <tr>
                    {showActions && (
                      <th style={{ width: 28 }}>
                        <input
                          type="checkbox"
                          checked={allChecked}
                          ref={(el) => { if (el) el.indeterminate = someChecked; }}
                          onChange={(e) => pickAll(e.target.checked)}
                        />
                      </th>
                    )}
                    <th>Kind</th>
                    <th>Name</th>
                    <th>Replicas</th>
                    <th>Containers</th>
                    <th className="actions">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {workloads.length === 0 && (
                    <tr><td className="empty" colSpan={showActions ? 6 : 5}>no workloads</td></tr>
                  )}
                  {workloads.map((w) => {
                    const isSelectedRow = selected?.kind === w.kind && selected.name === w.name;
                    const isPicked = picked.has(selKey(w));
                    return (
                      <tr key={`${w.kind}/${w.name}`} className={isSelectedRow ? "row-selected" : (isPicked ? "row-selected" : undefined)}>
                        {showActions && (
                          <td><input type="checkbox" checked={isPicked} onChange={() => togglePick(w)} /></td>
                        )}
                        <td className="mono">{w.kind}</td>
                        <td><b>{w.name}</b></td>
                        <td>
                          <ReplicaBadge ready={w.readyReplicas} desired={w.desiredReplicas} />
                        </td>
                        <td>
                          <div style={{ display: "flex", flexDirection: "column", gap: 2, maxWidth: 380 }}>
                            {w.containers.map((c) => (
                              <span
                                key={c.name}
                                className="mono"
                                style={{ fontSize: 11, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
                                title={`${c.name}: ${c.image}`}
                              >
                                <span className="faint">{c.name}:</span> {shortImage(c.image)}
                              </span>
                            ))}
                          </div>
                        </td>
                        <td className="actions">
                          <button className="btn btn-sm btn-ghost" onClick={() => setEventsFor({ kind: w.kind, name: w.name })}>
                            Events
                          </button>{" "}
                          <button className="btn btn-sm btn-primary" onClick={() => setSelected({ kind: w.kind, name: w.name })}>
                            Pods
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </section>

      {selected && <PodsPanel scope={scope} workload={selected} onClose={() => setSelected(null)} />}
      {eventsFor && <EventsPanel scope={scope} target={eventsFor} onClose={() => setEventsFor(null)} />}

      {scaleDialogOpen && (
        <ScaleDialog
          count={picked.size}
          onCancel={() => setScaleDialogOpen(false)}
          onConfirm={(n) => void bulkScale(n)}
        />
      )}
    </>
  );
}

function ScaleDialog({ count, onConfirm, onCancel }: { count: number; onConfirm: (replicas: number) => void; onCancel: () => void }) {
  const [replicas, setReplicas] = useState<string>("1");
  const parsed = Number.parseInt(replicas, 10);
  const valid = Number.isFinite(parsed) && parsed >= 0 && parsed <= 100;

  return (
    <Modal title={`Scale ${count} workload${count === 1 ? "" : "s"}`} onClose={onCancel}>
      <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
        <p className="faint" style={{ margin: 0, fontSize: 12 }}>
          Sets <span className="mono">spec.replicas</span> on each selected deployment / statefulset.
          Set to 0 to scale down. Max 100.
        </p>
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <span className="faint mono" style={{ fontSize: 10, letterSpacing: "0.18em" }}>REPLICAS</span>
          <input
            type="number"
            min={0}
            max={100}
            value={replicas}
            onChange={(e) => setReplicas(e.target.value)}
            style={{ width: 100 }}
            autoFocus
          />
        </div>
        <div className="btn-row">
          <button className="btn btn-primary" disabled={!valid} onClick={() => onConfirm(parsed)}>
            Apply
          </button>
          <button className="btn btn-ghost" onClick={onCancel}>Cancel</button>
        </div>
      </div>
    </Modal>
  );
}

function PodsPanel({ scope, workload, onClose }: { scope: Scope; workload: WorkloadSelection; onClose: () => void }) {
  const api = useApi();
  const [pods, setPods] = useState<PodSummary[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [activePodName, setActivePodName] = useState<string | null>(null);
  const [autoRefresh, setAutoRefresh] = useState(true);

  const load = useCallback(async () => {
    try {
      const next = await api.listPods(workload.kind, workload.name, scope);
      setPods(next);
      setError(null);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : `${e}`);
    }
  }, [api, workload.kind, workload.name, scope]);

  useEffect(() => { void load(); }, [load]);
  usePolling(load, 5000, autoRefresh);

  return (
    <section className="panel reveal" data-d="3">
      <header>
        <span className="idx">◇</span> Pods · {workload.kind}/{workload.name}
        <span className="hint">{autoRefresh ? "live · 5s" : "paused"}</span>
        <span className="spacer" />
        <button className="btn btn-ghost btn-sm" onClick={() => setAutoRefresh((v) => !v)}>{autoRefresh ? "Pause" : "Resume"}</button>{" "}
        <button className="btn btn-ghost btn-sm" onClick={() => void load()}><span className="glyph" /> Refresh</button>{" "}
        <button className="btn btn-ghost btn-sm" onClick={onClose}>Close</button>
      </header>
      <div className="body">
        {error && <Err msg={error} />}
        {!pods ? <Loading /> : (
          <div style={{ border: "1px solid var(--line)" }}>
            <table className="grid">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Phase</th>
                  <th>Ready</th>
                  <th>Restarts</th>
                  <th>Node</th>
                  <th className="actions">Actions</th>
                </tr>
              </thead>
              <tbody>
                {pods.length === 0 && <tr><td className="empty" colSpan={6}>no pods</td></tr>}
                {pods.map((p) => (
                  <tr key={p.name} className={activePodName === p.name ? "row-selected" : undefined}>
                    <td className="mono">{p.name}</td>
                    <td><PhaseBadge phase={p.phase} /></td>
                    <td>{p.ready ? "yes" : <span className="faint">no</span>}</td>
                    <td>{p.restartCount}</td>
                    <td className="mono faint" style={{ fontSize: 11 }}>{p.nodeName ?? "—"}</td>
                    <td className="actions">
                      <button className="btn btn-sm btn-primary" onClick={() => setActivePodName(p.name)}>
                        Inspect
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {activePodName && (
        <Modal title={`Pod · ${activePodName}`} onClose={() => setActivePodName(null)}>
          <PodDetailPanel scope={scope} name={activePodName} />
        </Modal>
      )}
    </section>
  );
}

function PodDetailPanel({ scope, name }: { scope: Scope; name: string }) {
  const api = useApi();
  const [detail, setDetail] = useState<PodDetail | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [logsContainer, setLogsContainer] = useState<string | null>(null);

  useEffect(() => {
    let cancel = false;
    setDetail(null);
    setLogsContainer(null);
    setError(null);
    void api.getPod(name, scope)
      .then((d) => { if (!cancel) setDetail(d); })
      .catch((e) => { if (!cancel) setError(e instanceof ApiError ? e.message : `${e}`); });
    return () => { cancel = true; };
  }, [api, name, scope]);

  if (error) return <Err msg={error} />;
  if (!detail) return <Loading />;

  return (
    <div>
      <dl className="spec">
        <dt>name</dt><dd className="mono">{detail.name}</dd>
        <dt>namespace</dt><dd className="mono">{detail.namespace}</dd>
        <dt>phase</dt><dd><PhaseBadge phase={detail.phase} /></dd>
        <dt>node</dt><dd className="mono">{detail.nodeName ?? "—"}</dd>
        <dt>pod ip</dt><dd className="mono">{detail.podIp ?? "—"}</dd>
        <dt>started</dt><dd className="mono">{fmtDate(detail.startTime)}</dd>
      </dl>

      {detail.containers.map((c) => (
        <div key={c.name} style={{ marginTop: 16, border: "1px solid var(--line)" }}>
          <div style={{ padding: "10px 14px", background: "var(--bg-panel)", borderBottom: "1px solid var(--line)", display: "flex", alignItems: "center" }}>
            <b className="mono">{c.name}</b>
            <span className="faint mono" style={{ fontSize: 11, marginLeft: 10 }}>{c.image}</span>
            <span className="spacer" style={{ flex: 1 }} />
            {c.status && <ContainerStateBadge state={c.status.state} reason={c.status.reason} />}
            <button
              className={"btn btn-sm " + (logsContainer === c.name ? "btn-primary" : "btn-ghost")}
              style={{ marginLeft: 10 }}
              onClick={() => setLogsContainer(logsContainer === c.name ? null : c.name)}
            >
              {logsContainer === c.name ? "Hide logs" : "Logs"}
            </button>
          </div>
          <div style={{ padding: 12 }}>
            <div className="faint mono" style={{ fontSize: 10, letterSpacing: "0.18em", marginBottom: 6 }}>ENV ({c.env.length})</div>
            {c.env.length === 0 ? (
              <span className="faint">no env vars</span>
            ) : (
              <table className="grid" style={{ fontSize: 11 }}>
                <thead><tr><th>Name</th><th>Value</th></tr></thead>
                <tbody>
                  {c.env.map((e) => (
                    <tr key={e.name}>
                      <td className="mono">{e.name}</td>
                      <td className="mono faint" style={{ wordBreak: "break-all" }}>
                        {e.value ?? <i>{e.valueFrom ?? "(unset)"}</i>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      ))}

      {logsContainer && <LogFollower scope={scope} podName={name} container={logsContainer} />}
    </div>
  );
}

function LogFollower({ scope, podName, container }: { scope: Scope; podName: string; container: string }) {
  const api = useApi();
  const [logs, setLogs] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [following, setFollowing] = useState(true);
  const [stickToBottom, setStickToBottom] = useState(true);
  const preRef = useRef<HTMLPreElement | null>(null);
  // Timestamp of the last successful fetch — drives the sinceSeconds parameter
  // on follow-up polls so we only download the new slice instead of the full
  // tail every 2s. null = no fetch yet, next call does the initial tail dump.
  const lastFetchAtRef = useRef<number | null>(null);
  const POLL_INTERVAL_MS = 2000;
  const INITIAL_TAIL_LINES = 1000;

  // Track whether the user is at the bottom of the log view; if so, snap to bottom on each
  // refresh. If they've scrolled up to read older content, leave them where they are.
  function handleScroll() {
    const el = preRef.current;
    if (!el) return;
    const atBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 4;
    setStickToBottom(atBottom);
  }

  const fetchLogs = useCallback(async () => {
    const now = Date.now();
    const lastFetchAt = lastFetchAtRef.current;
    try {
      let text: string;
      if (lastFetchAt === null) {
        // First fetch: grab the trailing N lines so the user sees backfill.
        text = await api.getPodLogs(podName, { container, tailLines: INITIAL_TAIL_LINES, scope });
        setLogs(text);
      } else {
        // Subsequent polls: only pull what's accumulated since the last fetch.
        // Add a 1s buffer so we don't miss anything at the boundary.
        const sinceSeconds = Math.max(1, Math.ceil((now - lastFetchAt) / 1000) + 1);
        text = await api.getPodLogs(podName, { container, sinceSeconds, scope });
        if (text.length > 0) {
          setLogs((prev) => (prev ?? "") + text);
        }
      }
      lastFetchAtRef.current = now;
      setError(null);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : `${e}`);
    }
  }, [api, podName, container, scope]);

  // Reset state when switching containers/pods.
  useEffect(() => {
    setLogs(null);
    setError(null);
    setFollowing(true);
    setStickToBottom(true);
    lastFetchAtRef.current = null;
    void fetchLogs();
  }, [fetchLogs]);

  usePolling(fetchLogs, POLL_INTERVAL_MS, following);

  // Auto-scroll to bottom on each update — but only if the user hasn't scrolled away.
  useEffect(() => {
    if (!stickToBottom) return;
    const el = preRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [logs, stickToBottom]);

  return (
    <div style={{ marginTop: 16 }}>
      <div style={{ display: "flex", alignItems: "center", marginBottom: 6 }}>
        <div className="faint mono" style={{ fontSize: 10, letterSpacing: "0.18em" }}>
          LOGS · {container} · last {INITIAL_TAIL_LINES} lines on open, incremental thereafter · {following ? "following · 2s" : "paused"}
        </div>
        <span style={{ flex: 1 }} />
        <button className="btn btn-sm btn-ghost" onClick={() => setFollowing((v) => !v)}>
          {following ? "Pause follow" : "Resume follow"}
        </button>{" "}
        <button className="btn btn-sm btn-ghost" onClick={() => {
          setStickToBottom(true);
          const el = preRef.current; if (el) el.scrollTop = el.scrollHeight;
        }}>
          Jump to end
        </button>
      </div>
      {error && <Err msg={error} />}
      <pre
        ref={preRef}
        onScroll={handleScroll}
        style={{ margin: 0, padding: 12, height: 420, overflow: "auto", background: "#0a1214", border: "1px solid var(--line)", fontSize: 11, fontFamily: "var(--font-mono)", whiteSpace: "pre-wrap" }}
      >
        {logs ?? "loading…"}
      </pre>
    </div>
  );
}

function EventsPanel({ scope, target, onClose }: { scope: Scope; target: EventsFor; onClose: () => void }) {
  const api = useApi();
  const [events, setEvents] = useState<K8sEvent[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setError(null);
    try {
      setEvents(await api.listEvents(target.kind, target.name, scope));
    } catch (e) {
      setError(e instanceof ApiError ? e.message : `${e}`);
    }
  }, [api, target.kind, target.name, scope]);

  useEffect(() => { void load(); }, [load]);

  return (
    <section className="panel reveal" data-d="3">
      <header>
        <span className="idx">≡</span> Events · {target.kind}/{target.name}
        <span className="spacer" />
        <button className="btn btn-ghost btn-sm" onClick={() => void load()}><span className="glyph" /> Refresh</button>{" "}
        <button className="btn btn-ghost btn-sm" onClick={onClose}>Close</button>
      </header>
      <div className="body">
        {error && <Err msg={error} />}
        {!events ? <Loading /> : events.length === 0 ? <span className="faint">no events</span> : (
          <div style={{ border: "1px solid var(--line)" }}>
            <table className="grid">
              <thead>
                <tr>
                  <th>Type</th>
                  <th>Reason</th>
                  <th>Message</th>
                  <th>×</th>
                  <th>Last</th>
                </tr>
              </thead>
              <tbody>
                {events.map((e, i) => (
                  <tr key={i}>
                    <td><EventTypeBadge type={e.type} /></td>
                    <td className="mono">{e.reason}</td>
                    <td style={{ fontSize: 11 }}>{e.message}</td>
                    <td>{e.count}</td>
                    <td className="mono faint" style={{ fontSize: 11 }}>{fmtRelative(e.lastTimestamp)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </section>
  );
}

function IngressesPanel() {
  const api = useApi();
  const [ingresses, setIngresses] = useState<Ingress[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setError(null);
    try {
      setIngresses(await api.listIngresses());
    } catch (e) {
      setError(e instanceof ApiError ? e.message : `${e}`);
    }
  }, [api]);

  useEffect(() => { void load(); }, [load]);

  return (
    <section className="panel reveal" data-d="2">
      <header>
        <span className="idx">01</span> Ingresses
        <span className="hint">parent ingress topology (host → service)</span>
        <span className="spacer" />
        <button className="btn btn-ghost btn-sm" onClick={() => void load()}><span className="glyph" /> Refresh</button>
      </header>
      <div className="body">
        {error && <Err msg={error} />}
        {!ingresses ? <Loading /> : ingresses.length === 0 ? <span className="faint">no ingresses</span> : (
          <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
            {ingresses.map((ing) => (
              <div key={ing.name} style={{ border: "1px solid var(--line)" }}>
                <div style={{ padding: "10px 14px", background: "var(--bg-panel-2)", borderBottom: "1px solid var(--line)" }}>
                  <b className="mono">{ing.name}</b>
                  {ing.ingressClassName && <span className="faint mono" style={{ fontSize: 11, marginLeft: 10 }}>class={ing.ingressClassName}</span>}
                  {ing.loadBalancerIp && <span className="faint mono" style={{ fontSize: 11, marginLeft: 10 }}>lb={ing.loadBalancerIp}</span>}
                </div>
                <table className="grid" style={{ fontSize: 12, tableLayout: "fixed", width: "100%" }}>
                  <colgroup>
                    <col style={{ width: "45%" }} />
                    <col style={{ width: "20%" }} />
                    <col style={{ width: "25%" }} />
                    <col style={{ width: "10%" }} />
                  </colgroup>
                  <thead>
                    <tr>
                      <th>Host</th>
                      <th>Path</th>
                      <th style={{ textAlign: "right" }}>Service</th>
                      <th style={{ textAlign: "right" }}>Port</th>
                    </tr>
                  </thead>
                  <tbody>
                    {ing.rules.flatMap((r) => r.paths.map((p, i) => (
                      <tr key={`${r.host}/${p.path}/${i}`}>
                        <td className="mono" style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={r.host}>{r.host}</td>
                        <td className="mono faint">{p.path}</td>
                        <td className="mono" style={{ textAlign: "right", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={p.serviceName}>{p.serviceName}</td>
                        <td className="mono faint" style={{ textAlign: "right" }}>{p.servicePort ?? "—"}</td>
                      </tr>
                    )))}
                  </tbody>
                </table>
              </div>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}

function CertificatesPanel() {
  const api = useApi();
  const [certs, setCerts] = useState<Certificate[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setError(null);
    try {
      setCerts(await api.listCertificates());
    } catch (e) {
      setError(e instanceof ApiError ? e.message : `${e}`);
    }
  }, [api]);

  useEffect(() => { void load(); }, [load]);

  const sorted = useMemo(() => certs ? [...certs].sort((a, b) => {
    const aT = a.notAfter ? Date.parse(a.notAfter) : Number.MAX_SAFE_INTEGER;
    const bT = b.notAfter ? Date.parse(b.notAfter) : Number.MAX_SAFE_INTEGER;
    return aT - bT;
  }) : null, [certs]);

  return (
    <section className="panel reveal" data-d="2">
      <header>
        <span className="idx">01</span> Certificates
        <span className="hint">cert-manager — expiry first</span>
        <span className="spacer" />
        <button className="btn btn-ghost btn-sm" onClick={() => void load()}><span className="glyph" /> Refresh</button>
      </header>
      <div className="body">
        {error && <Err msg={error} />}
        {!sorted ? <Loading /> : sorted.length === 0 ? <span className="faint">no certificates</span> : (
          <div style={{ border: "1px solid var(--line)" }}>
            <table className="grid">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Ready</th>
                  <th>DNS</th>
                  <th>Expires</th>
                  <th>Renewal</th>
                </tr>
              </thead>
              <tbody>
                {sorted.map((c) => (
                  <tr key={c.name}>
                    <td className="mono">{c.name}</td>
                    <td><CertReadyBadge ready={c.ready} reason={c.readyReason} /></td>
                    <td className="mono faint" style={{ fontSize: 11 }}>{c.dnsNames.join(", ") || "—"}</td>
                    <td className="mono"><ExpiryBadge notAfter={c.notAfter} /></td>
                    <td className="mono faint" style={{ fontSize: 11 }}>{fmtDate(c.renewalTime)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </section>
  );
}

// ---------- bits ----------

function Loading() {
  return <div className="faint mono" style={{ fontSize: 11, letterSpacing: "0.16em" }}>loading…</div>;
}
function Err({ msg }: { msg: string }) {
  return (
    <div className="callout error" style={{ marginBottom: 14 }}>
      <span className="mk">err</span>
      <span>{msg}</span>
    </div>
  );
}

function ReplicaBadge({ ready, desired }: { ready: number; desired: number }) {
  const ok = ready === desired && desired > 0;
  const partial = ready > 0 && ready < desired;
  const color = ok ? "var(--lime)" : partial ? "var(--amber)" : "var(--ink-faint)";
  return (
    <span className="mono" style={{ color, fontWeight: 600 }}>
      {ready}/{desired}
    </span>
  );
}

function PhaseBadge({ phase }: { phase: string }) {
  const color =
    phase === "Running" ? "var(--lime)" :
    phase === "Pending" ? "var(--amber)" :
    phase === "Succeeded" ? "var(--cyan)" :
    phase === "Failed" ? "var(--red)" :
    "var(--ink-faint)";
  return <span className="mono" style={{ color, fontWeight: 600 }}>{phase}</span>;
}

function ContainerStateBadge({ state, reason }: { state: string; reason: string | null }) {
  const color =
    state === "running" ? "var(--lime)" :
    state === "waiting" ? "var(--amber)" :
    state === "terminated" ? "var(--red)" :
    "var(--ink-faint)";
  return (
    <span className="mono" style={{ color, fontSize: 11 }}>
      {state}{reason ? ` · ${reason}` : ""}
    </span>
  );
}

function EventTypeBadge({ type }: { type: string }) {
  const color = type === "Warning" ? "var(--amber)" : "var(--ink-dim)";
  return <span className="mono" style={{ color, fontSize: 11 }}>{type}</span>;
}

function CertReadyBadge({ ready, reason }: { ready: string | null; reason: string | null }) {
  if (ready === "True") return <span className="mono" style={{ color: "var(--lime)" }}>ready</span>;
  if (ready === "False") return <span className="mono" style={{ color: "var(--red)", fontSize: 11 }}>not ready{reason ? ` · ${reason}` : ""}</span>;
  return <span className="faint mono">—</span>;
}

function ExpiryBadge({ notAfter }: { notAfter: string | null }) {
  if (!notAfter) return <span className="faint">—</span>;
  const d = new Date(notAfter);
  const days = Math.floor((d.getTime() - Date.now()) / (1000 * 60 * 60 * 24));
  const color = days < 7 ? "var(--red)" : days < 21 ? "var(--amber)" : "var(--ink-dim)";
  return (
    <span style={{ color }}>
      {fmtDate(notAfter)}
      <span className="faint" style={{ marginLeft: 8, fontSize: 11 }}>({days}d)</span>
    </span>
  );
}

function shortImage(image: string): string {
  // Strip the registry prefix; keep repo+tag.
  const slash = image.lastIndexOf("/");
  return slash >= 0 ? image.substring(slash + 1) : image;
}

function fmtDate(s: string | null): string {
  if (!s) return "—";
  try {
    return new Date(s).toISOString().replace("T", " ").substring(0, 19) + "Z";
  } catch {
    return s;
  }
}

function fmtRelative(s: string | null): string {
  if (!s) return "—";
  const t = new Date(s).getTime();
  const diff = Date.now() - t;
  const sec = Math.floor(diff / 1000);
  if (sec < 60) return `${sec}s ago`;
  const min = Math.floor(sec / 60);
  if (min < 60) return `${min}m ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}h ago`;
  return `${Math.floor(hr / 24)}d ago`;
}
